import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EvaluationLayout from '../../components/EvaluationLayout';
import {
  ArrowLeft,
  BarChart3,
  Clock,
  DollarSign,
  Zap,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  Mail,
  Send,
  Calendar,
  Eye,
  EyeOff
} from 'lucide-react';
import Button from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import Badge from '../../components/ui/badge';
import { formatDate } from '../../lib/utils';
import evaluationService from '../../services/evaluationService';
import { toast } from 'react-hot-toast';
import ScheduleModal from '../../components/ScheduleModal';
import SendEmailModal from '../../components/SendEmailModal';
import SendWebhookModal from '../../components/SendWebhookModal';

const RunResultsView = () => {
  const { runId } = useParams();
  const navigate = useNavigate();

  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showRawData, setShowRawData] = useState({});

  // Modal states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);

  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'running':
        return <Clock className="w-4 h-4 animate-spin text-blue-500" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  }, []);

  const getStatusBadge = useCallback((status) => {
    const variants = {
      running: 'default',
      completed: 'secondary',
      failed: 'destructive',
      cancelled: 'outline'
    };

    return (
      <Badge variant={variants[status] || 'outline'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  }, []);

  const loadRunDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await evaluationService.getEvaluationRun(runId);

      if (response.success) {
        setRun(response.data);
      } else {
        toast.error('Failed to load run details');
        navigate('/evaluations');
      }
    } catch (error) {
      console.error('Failed to load run details:', error);
      toast.error('Failed to load run details');
      navigate('/evaluations');
    } finally {
      setLoading(false);
    }
  }, [runId, navigate]);

  useEffect(() => {
    loadRunDetails();
  }, [runId, loadRunDetails]);

  const toggleRawData = (testIndex) => {
    setShowRawData(prev => ({
      ...prev,
      [testIndex]: !prev[testIndex]
    }));
  };

  const handleSchedule = async (scheduleData) => {
    try {
      const response = await evaluationService.scheduleFromRun(run.run_id, scheduleData);

      if (response.success) {
        toast.success('Evaluation scheduled successfully');
      } else {
        toast.error(response.error || 'Failed to schedule evaluation');
      }
    } catch (error) {
      console.error('Failed to schedule from run:', error);
      toast.error('Failed to schedule evaluation');
      throw error;
    }
  };

  const handleSendEmail = async (emailData) => {
    try {
      const response = await evaluationService.sendRunEmail(run.run_id, emailData);

      if (response.success) {
        toast.success('Email sent successfully');
      } else {
        toast.error(response.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error('Failed to send email');
      throw error;
    }
  };

  const handleSendWebhook = async (webhookData) => {
    try {
      const response = await evaluationService.sendRunWebhook(run.run_id, webhookData);

      if (response.success) {
        toast.success('Webhook sent successfully');
      } else {
        toast.error(response.error || 'Failed to send webhook');
      }
    } catch (error) {
      console.error('Failed to send webhook:', error);
      toast.error('Failed to send webhook');
      throw error;
    }
  };

  if (loading) {
    return (
      <EvaluationLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Clock className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Loading run details...</p>
          </div>
        </div>
      </EvaluationLayout>
    );
  }

  if (!run) {
    return (
      <EvaluationLayout>
        <div className="text-center py-12">
          <XCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Run not found</h3>
          <p className="text-muted-foreground mb-4">The evaluation run you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/evaluations')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Evaluations
          </Button>
        </div>
      </EvaluationLayout>
    );
  }

  const results = run.results || {};
  const summary = results.summary || {};
  const testCases = results.test_cases || [];
  const metrics = results.metrics || {};

  return (
    <EvaluationLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/evaluations')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">{run.evaluation_name}</h1>
              <div className="flex items-center space-x-4 mt-1">
                <p className="text-muted-foreground">Run ID: {run.run_id}</p>
                {getStatusIcon(run.status)}
                {getStatusBadge(run.status)}
              </div>
            </div>
          </div>

          <div className="flex space-x-3">
            {run.status === 'completed' && (
              <>
                <Button variant="outline" onClick={() => setShowScheduleModal(true)}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule
                </Button>
                <Button variant="outline" onClick={() => setShowEmailModal(true)}>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
                <Button variant="outline" onClick={() => setShowWebhookModal(true)}>
                  <Send className="w-4 h-4 mr-2" />
                  Send Webhook
                </Button>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="test-cases">Test Cases</TabsTrigger>
            <TabsTrigger value="metrics">Metrics</TabsTrigger>
            {run.status === 'failed' && <TabsTrigger value="errors">Errors</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {run.status === 'completed' && (
                <>
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <BarChart3 className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Overall Score</p>
                          <p className="text-2xl font-bold text-foreground">
                            {run.results?.summary?.average_score !== undefined 
                              ? Math.round(run.results.summary.average_score * 100) 
                              : 'N/A'}%
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-500/10 rounded-lg">
                          <CheckCircle className="w-6 h-6 text-green-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Success Rate</p>
                          <p className="text-2xl font-bold text-foreground">
                            {summary.total_tests > 0 ?
                              Math.round((summary.passed_tests / summary.total_tests) * 100) : 0}%
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-500/10 rounded-lg">
                          <Clock className="w-6 h-6 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Avg Response Time</p>
                          <p className="text-2xl font-bold text-foreground">
                            {metrics.average_response_time ?
                              Math.round(metrics.average_response_time) + 'ms' : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-yellow-500/10 rounded-lg">
                          <DollarSign className="w-6 h-6 text-yellow-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Cost</p>
                          <p className="text-2xl font-bold text-foreground">
                            ${metrics.total_cost ? metrics.total_cost.toFixed(4) : '0.0000'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {/* Run Details */}
            <Card>
              <CardHeader>
                <CardTitle>Run Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Model</p>
                    <p className="font-medium">{run.execution_details?.model || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Provider</p>
                    <p className="font-medium">{run.execution_details?.provider || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Trigger</p>
                    <p className="font-medium">{run.trigger}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Started</p>
                    <p className="font-medium">
                      {run.execution_details?.started_at ?
                        formatDate(run.execution_details.started_at) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="font-medium">
                      {run.execution_details?.completed_at ?
                        formatDate(run.execution_details.completed_at) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Duration</p>
                    <p className="font-medium">
                      {run.execution_details?.duration_ms ?
                        Math.round(run.execution_details.duration_ms / 1000) + 's' : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="test-cases">
            <Card>
              <CardHeader>
                <CardTitle>Test Case Results</CardTitle>
              </CardHeader>
              <CardContent>
                {testCases.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No test case data available</p>
                ) : (
                  <div className="space-y-4">
                    {testCases.map((testCase, index) => (
                      <div key={index} className="border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <h4 className="font-medium">Test Case {index + 1}</h4>
                            {testCase.passed ? (
                              <Badge variant="secondary">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Passed
                              </Badge>
                            ) : (
                              <Badge variant="destructive">
                                <XCircle className="w-3 h-3 mr-1" />
                                Failed
                              </Badge>
                            )}
                            {testCase.overall_score && (
                              <Badge variant="outline">
                                Score: {Math.round(testCase.overall_score * 100)}%
                              </Badge>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleRawData(index)}
                          >
                            {showRawData[index] ? (
                              <>
                                <EyeOff className="w-4 h-4 mr-1" />
                                Hide Details
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-1" />
                                Show Details
                              </>
                            )}
                          </Button>
                        </div>

                        {showRawData[index] && (
                          <div className="space-y-3 mt-4 pt-4 border-t border-border">
                            {testCase.input && (
                              <div>
                                <p className="text-sm font-medium text-muted-foreground mb-1">Input:</p>
                                <pre className="text-xs bg-secondary/50 p-2 rounded overflow-x-auto">
                                  {JSON.stringify(testCase.input, null, 2)}
                                </pre>
                              </div>
                            )}
                            {testCase.actual && (
                              <div>
                                <p className="text-sm font-medium text-muted-foreground mb-1">Output:</p>
                                <pre className="text-xs bg-secondary/50 p-2 rounded overflow-x-auto">
                                  {JSON.stringify(testCase.actual, null, 2)}
                                </pre>
                              </div>
                            )}
                            {testCase.expected && (
                              <div>
                                <p className="text-sm font-medium text-muted-foreground mb-1">Expected:</p>
                                <pre className="text-xs bg-secondary/50 p-2 rounded overflow-x-auto">
                                  {JSON.stringify(testCase.expected, null, 2)}
                                </pre>
                              </div>
                            )}
                            {testCase.error && (
                              <div>
                                <p className="text-sm font-medium text-destructive mb-1">Error:</p>
                                <p className="text-xs text-destructive bg-destructive/10 p-2 rounded">
                                  {testCase.error}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="metrics">
            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-secondary/50 rounded-lg">
                    <Zap className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">
                      {metrics.total_tokens ? metrics.total_tokens.toLocaleString() : '0'}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Tokens</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/50 rounded-lg">
                    <DollarSign className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">
                      ${metrics.total_cost ? metrics.total_cost.toFixed(4) : '0.0000'}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Cost</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/50 rounded-lg">
                    <Clock className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">
                      {metrics.average_response_time ? Math.round(metrics.average_response_time) : '0'}ms
                    </p>
                    <p className="text-sm text-muted-foreground">Avg Response Time</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/50 rounded-lg">
                    <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">
                      {metrics.error_rate ? Math.round(metrics.error_rate) : '0'}%
                    </p>
                    <p className="text-sm text-muted-foreground">Error Rate</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/50 rounded-lg">
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">
                      {summary.passed_tests || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Passed Tests</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/50 rounded-lg">
                    <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">
                      {summary.failed_tests || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Failed Tests</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {run.status === 'failed' && (
            <TabsContent value="errors">
              <Card>
                <CardHeader>
                  <CardTitle>Error Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">Error Type:</p>
                      <p className="text-destructive font-medium">
                        {run.error_details?.error_type || 'Unknown Error'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">Error Message:</p>
                      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                        <p className="text-destructive">
                          {run.error_details?.error_message || 'No error message available'}
                        </p>
                      </div>
                    </div>
                    {run.error_details?.failed_at && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">Failed At:</p>
                        <p className="text-foreground">
                          {formatDate(run.error_details.failed_at)}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Modals */}
        <ScheduleModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onSchedule={handleSchedule}
          runData={run}
        />

        <SendEmailModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onSendEmail={handleSendEmail}
          runData={run}
        />

        <SendWebhookModal
          isOpen={showWebhookModal}
          onClose={() => setShowWebhookModal(false)}
          onSendWebhook={handleSendWebhook}
          runData={run}
        />
      </div>
    </EvaluationLayout>
  );
};

export default RunResultsView;
